
import { defineComponent } from "vue";
import FloatingLabel from "@/components/shared/FloatingLabel.vue";
import Spinner from "@/components/shared/Spinner.vue";
import { PbAddressResponse, PbUnit } from "@/types";
import { get } from "lodash-es";
import { preciselyAddressResult } from "@/util/locationFactory";
import { useLocation, usePrecisely } from "@/services";

export default defineComponent({
  name: "PreciselySearch",
  components: {
    FloatingLabel,
    Spinner,
  },
  data: () => ({
    resultIndex: null as null | number,
    unitIndex: null as null | number,
    selectedResult: null as null | PbAddressResponse,
    searchResults: [] as PbAddressResponse[],
    isLoadingResults: false,
    provider: {} as { access_token?: string },
  }),
  computed: {
    searchText: {
      get(): string {
        return usePrecisely().searchText;
      },
      set(value: string) {
        if (value !== this.searchText) {
          usePrecisely().searchText = value;
        }
      },
    },
    formattedAddress(): string | undefined {
      return (
        get(this, "selectedResult.address.formattedAddress") ||
        (typeof this.resultIndex === "number" &&
          get(
            this.searchResults,
            `${this.resultIndex}.address.formattedAddress`
          )) ||
        useLocation().formattedAddress ||
        this.searchText
      );
    },
    units(): string[] {
      return this.selectedResult
        ? ([] as PbUnit[])
            .concat(...this.selectedResult.ranges.map((r) => r.units))
            .map((u) => u.formattedUnitAddress)
        : [];
    },
  },
  watch: {
    searchText(val) {
      this.getResults(val);
    },
  },
  methods: {
    async searchAddress(searchText: string) {
      this.isLoadingResults = true;
      return searchText?.length > 2
        ? usePrecisely()
            .searchAddress(searchText)
            .then((r) => {
              const results = r.data.location;
              if (!results.length) {
                return this.forwardGeocode(searchText);
              } else {
                this.isLoadingResults = false;
                return r.data.location;
              }
            })
        : Promise.resolve({ data: { location: [] } }).then(() => {
            this.isLoadingResults = false;
          });
    },
    forwardGeocode(searchText: string) {
      return usePrecisely()
        .forwardGeocode(searchText)
        .then((r) => {
          this.isLoadingResults = false;
          switch (get(r.data, "candidates.length")) {
            case 0:
              // ToDo: handle no candidates
              console.log("Bad data => no candidates => ", r.data);
              break;
            case 1:
              this.updateAddressLocation(r.data.candidates[0]);
              break;
            default:
              // ToDo: handle multiple candidates
              this.updateAddressLocation(r.data.candidates[0]);
              console.log(
                "Bad data => multiple candidates => ",
                r.data.candidates
              );
          }
          this.resetSearch();
          return get(r.data, "candidates") || [];
        });
    },
    getResults(searchText: string) {
      this.unitIndex = null;
      this.resultIndex = null;
      if (searchText) {
        this.searchAddress(searchText).then((r) => {
          this.searchResults = r || [];
        });
      } else {
        this.searchResults = [];
      }
    },
    changeSelection(down = false) {
      if (this.selectedResult) {
        this.unitIndex =
          typeof this.unitIndex === "number"
            ? (this.units.length + this.unitIndex - (down ? 1 : -1)) %
              this.units.length
            : 0;
      } else {
        this.resultIndex =
          typeof this.resultIndex === "number"
            ? (this.searchResults.length + this.resultIndex - (down ? 1 : -1)) %
              this.searchResults.length
            : 0;
      }
      this.$nextTick(() => {
        const el = this.$el?.querySelector(".is-selected");
        const container = el?.closest(".address-search-results");
        if (
          el instanceof HTMLAnchorElement &&
          container instanceof HTMLDivElement
        ) {
          el.scrollIntoView(true);
        }
      });
    },
    selectAddress(index?: number) {
      if (this.isLoadingResults) {
        // ToDo: exiting here is probably wrong. Cancel previous request and proceed.
        return;
      }
      if (this.units.length) {
        this.selectUnit(index);
      } else if (typeof index === "number") {
        this.resultIndex = index;
        this.selectedResult = this.searchResults[this.resultIndex];
        if (!this.units.length) {
          this.selectAddress();
        }
        if (this.units.length === 1) {
          this.selectAddress(0);
        }
      } else {
        if (this.formattedAddress) {
          this.forwardGeocode(this.formattedAddress);
        }
      }
    },
    selectUnit(index?: number) {
      if (typeof index === "number") {
        this.unitIndex = index;
      }
      usePrecisely()
        .forwardGeocode(this.units[this.unitIndex || 0])
        .then((r) => {
          switch (get(r.data, "candidates.length")) {
            case 0:
              // ToDo: handle no candidates
              console.log("Bad data => no candidates => ", r.data);
              break;
            case 1:
              this.updateAddressLocation(r.data.candidates[0]);
              break;
            default:
              // ToDo: handle multiple candidates
              this.updateAddressLocation(r.data.candidates[0]);
              console.log(
                "Bad data => multiple candidates => ",
                r.data.candidates
              );
          }
          this.resetSearch();
        });
    },
    resetSearch() {
      this.unitIndex = null;
      this.resultIndex = null;
      this.selectedResult = null;
      this.searchResults = [];
    },
    updateAddressLocation(address: PbAddressResponse): void {
      const location = preciselyAddressResult(address);
      useLocation().updateLocation(location);
    },
  },
});
